import { useId, useState } from 'react';
import { Icon } from '../editor/chrome/icons';
import './projectFolderArt.css';
import { tileUrl } from '../editor/content/tileUrl';

/** A project's main brand, as the badge on its folder. */
export interface FolderBrand {
  readonly name?: string | null;
  readonly logo?: string | null;
  readonly cover?: string | null;
}

/**
 * The photo pocket on the Home's Projects row, the Projects page and in My Stuff: a neutral folder
 * holding pictures, the front one first. Never a project colour. A project with a brand wears the
 * brand's logo as a small tile on the folder's lower right (Tarek, 13 Oct).
 */
export function ProjectFolderArt({ covers = [], brand = null }: { covers?: readonly (string | null)[]; brand?: FolderBrand | null }) {
  const id = useId().replace(/:/g, '');
  return (
    <span className="project-folder-art" aria-hidden="true">
      <svg className="project-folder-back" viewBox="0 0 128 100">
        <path d="M4 12.5 Q4 6 10.5 6 H39 Q43.5 6 46.5 9 L48.5 11 Q49.6 12 51.4 12 H117.5 Q124 12 124 18.5 V88 Q124 96 116 96 H12 Q4 96 4 88 Z" fill="var(--folder-back)" />
      </svg>
      {covers.length > 0 ? <span className="project-folder-prints">
        {covers.slice(0, 3).map((url, index) => (
          <span key={`${index}:${url ?? ''}`} className="project-folder-print" data-depth={index}>
            <FolderCover url={url} />
          </span>
        ))}
      </span> : null}
      <svg className="project-folder-front" viewBox="0 0 128 100">
        <defs>
          <linearGradient id={`${id}front`} gradientUnits="userSpaceOnUse" x1="0" y1="52" x2="0" y2="96">
            <stop offset="0" stopColor="var(--folder-lip)" />
            <stop offset=".02" stopColor="var(--folder-lip)" />
            <stop offset=".036" stopColor="var(--folder-body)" />
            <stop offset=".82" stopColor="var(--folder-base)" />
            <stop offset="1" stopColor="var(--folder-deep)" />
          </linearGradient>
        </defs>
        <path d="M4 54.5 Q4 52 6.5 52 H121.5 Q124 52 124 54.5 V88 Q124 96 116 96 H12 Q4 96 4 88 Z" fill={`url(#${id}front)`} />
      </svg>
      {brand ? <BrandBadge brand={brand} /> : null}
    </span>
  );
}

/** Logo on white; no logo → the brand's cover; neither → its initial. */
function BrandBadge({ brand }: { brand: FolderBrand }) {
  const [broken, setBroken] = useState(false);
  const pic = broken ? null : brand.logo || brand.cover || null;
  return (
    <span className="project-folder-brand" title={brand.name || undefined}>
      {pic ? (
        <img src={tileUrl(pic)} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} data-logo={brand.logo && !broken ? '' : undefined} />
      ) : (
        (brand.name || '?').slice(0, 1).toUpperCase()
      )}
    </span>
  );
}

function FolderCover({ url }: { url: string | null }) {
  const [broken, setBroken] = useState(false);
  return url && !broken
    ? <img src={tileUrl(url, 640)} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} />
    : <span className="project-folder-cover-missing"><Icon name="artboard" size={16} /></span>;
}
